/**
 * Root WebSocket saga — single persistent connection, feature-plugin dispatch.
 *
 * Lifecycle: connect on boot (if token present) or on auth/loginSuccess;
 * disconnect on auth/logout. One connection at a time.
 *
 * Infra owns the connection + routing table. Features own message-type → action
 * mappings, declared in their own `ws-plugin.ts` file and passed through
 * registerWsPlugin() at store-wiring time.
 */

import { Task, eventChannel, EventChannel } from 'redux-saga';
import { call, cancel, cancelled, delay, fork, put, take } from 'redux-saga/effects';
import { createWebSocketChannel, WebSocketChannel } from './channel';
import { WsMessage } from './types';
import { emitPoint, finalizeFlow, pushActiveFlow, startFlow } from '@/features/qpl-client';
import { APP_RESUMED } from '@/infra/sync/actions';

export type WsPluginHandler = (msg: WsMessage) => { type: string; payload: unknown } | null;
export type WsPluginMap = Record<string, WsPluginHandler>;

const registry: WsPluginMap = {};

export function registerWsPlugin(handlers: WsPluginMap): void {
  Object.assign(registry, handlers);
}

function getWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

function bridge(channel: WebSocketChannel): EventChannel<WsMessage> {
  return eventChannel<WsMessage>((emit) => {
    channel.take((msg) => emit(msg));
    return () => channel.close();
  });
}

/**
 * Open a per-message QPL flow, run the handler (and any action it
 * dispatches) inside it, then finalize. The shared flow-ref holder is
 * set active so the axios interceptor stamps X-QPL-ID on any HTTP
 * calls that sagas triggered by the dispatched action make. On
 * completion the holder is cleared so subsequent work falls back to
 * whichever navigation flow is active.
 */
function* dispatchWithFlow(
  msg: WsMessage,
  handler: WsPluginHandler,
): Generator<unknown, void, unknown> {
  // Capture the full inbound message as flow annotations — msg.type alone
  // only tells you "something called task.updated arrived", which the flow
  // row name already says. The real debug signal is msg.data (which task,
  // which fields changed) and msg.meta (server request_id for correlating
  // with API logs).
  const handle = startFlow(`ws.${msg.type}`, {
    msg_type: msg.type,
    data: msg.data,
    meta: msg.meta,
  });
  const restore = pushActiveFlow(handle.id, `ws.${msg.type}`);
  try {
    const action = handler(msg);
    if (action) {
      // Record the full dispatched redux action (type + payload) so the
      // dashboard can show what actually hit the store. Previously only
      // action.type was captured, which duplicated the msg.type signal.
      emitPoint(handle.id, 'ws_dispatch_begin', {
        action_type: action.type,
        action_payload: action.payload,
      });
      yield put(action);
      emitPoint(handle.id, 'ws_dispatch_end', { action_type: action.type });
    }
    finalizeFlow(handle.id, 'SUCCESS');
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    emitPoint(handle.id, 'ws_dispatch_error', { message });
    finalizeFlow(handle.id, 'FAILED');
    throw e;
  } finally {
    restore();
  }
}

/**
 * Published when the socket reconnects after a drop. Features that hold
 * server-authoritative data (todos, calendar, household) should re-fetch
 * on this action — any broadcasts emitted while disconnected are lost,
 * and without a refetch the store stays silently stale.
 */
export const WS_RECONNECTED = 'ws/reconnected';

function* connectionLoop(): Generator {
  let attempt = 0;
  while (true) {
    // BFF auth: the chores_session cookie rides the WebSocket upgrade
    // request the same way it rides any other same-origin fetch. We
    // pass an empty token so extractWSToken on the server falls
    // through to the cookie path (chores' sessionMiddleware rewrites
    // Authorization on the upgrade request itself; the original
    // extractWSToken now finds a bearer where there was none).
    const channel: WebSocketChannel = yield call(createWebSocketChannel, {
      url: getWsUrl(),
      token: '',
    });
    const evt: EventChannel<WsMessage> = yield call(bridge, channel);

    let disconnected = false;
    try {
      while (!disconnected) {
        const msg = (yield take(evt)) as WsMessage;
        if (msg.type === 'WS_CONNECTED') {
          if (attempt > 0) {
            yield put({ type: WS_RECONNECTED });
          }
          attempt = 0;
          continue;
        }
        if (msg.type === 'WS_DISCONNECTED') {
          disconnected = true;
          continue;
        }
        if (msg.type === 'WS_ERROR') {
          // Error is always followed by close; wait for that to trigger
          // reconnect instead of racing it here.
          continue;
        }
        const handler = registry[msg.type];
        if (!handler) continue;
        yield call(dispatchWithFlow, msg, handler);
      }
    } finally {
      evt.close();
      channel.close();
      if (yield cancelled()) {
        return;
      }
    }

    // Exponential backoff: 1s, 2s, 4s, ... capped at 30s. Resets to 0 on
    // WS_CONNECTED above so a flaky network that finally stabilizes doesn't
    // keep the cap after things normalize.
    attempt += 1;
    const delayMs = Math.min(30000, 1000 * Math.pow(2, attempt - 1));
    yield delay(delayMs);
  }
}

export function* wsRootSaga(): Generator {
  let task: Task | null = null;

  // No boot-time connect. The auth saga dispatches loginSuccess once
  // GET /api/v1/auth/me succeeds — only then does the SPA know it has
  // a usable cookie and can safely upgrade.

  while (true) {
    const action = (yield take(['auth/loginSuccess', 'auth/logout', APP_RESUMED])) as {
      type: string;
    };

    // A suspension kills the socket, but the browser does not reliably
    // deliver the close event for it: the connection loop can sit
    // blocked on `take(evt)` indefinitely, holding a connection that
    // will never carry another frame. Cancelling forces the channel's
    // finally-block teardown and a fresh dial — and because the new loop
    // starts at attempt 0, it re-dials immediately instead of inheriting
    // the old loop's backoff.
    if (action.type === APP_RESUMED) {
      if (!task) continue; // not connected (signed out) — nothing to redial
      yield cancel(task);
      task = (yield fork(connectionLoop)) as Task;
      continue;
    }

    if (task) {
      yield cancel(task);
      task = null;
    }

    if (action.type === 'auth/loginSuccess') {
      task = (yield fork(connectionLoop)) as Task;
    }
  }
}
